import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";

import { probeIdentity } from "./api";
import { clearToken } from "./auth";
import { setUnauthorizedHandler } from "./authGuard";
import { EMPTY_GRANTS, type Grants } from "./scopes";

interface SessionState {
  grants: Grants;
  subject: string;
  loading: boolean;
  error: string;
  reload: () => void;
}

const SessionContext = createContext<SessionState | null>(null);

interface Props {
  children: ReactNode;
  /** Called when the identity endpoint itself refuses the token. */
  onSessionLost: () => void;
}

/**
 * Resolves the caller's grants once and holds them for the whole authenticated
 * shell, and owns the "was that 401 the session or just the route?" decision.
 *
 * Rendering is gated until the grants are known — a route that mounts with
 * unknown scopes would flash controls the caller may not use, and a nav item
 * that appears and disappears reads as a bug.
 */
export function SessionProvider({ children, onSessionLost }: Props) {
  const [grants, setGrants] = useState<Grants>(EMPTY_GRANTS);
  const [subject, setSubject] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // A single in-flight re-probe: one page can fire several parallel calls that
  // all 401, and each must not start its own probe.
  const probing = useRef(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const probe = await probeIdentity();
    if (probe.state === "ok") {
      setGrants({ scopes: probe.me.scopes, realms: probe.me.realms });
      setSubject(probe.me.subject);
      setLoading(false);
      return;
    }
    setLoading(false);
    if (probe.state === "unauthorized") {
      clearToken();
      onSessionLost();
      return;
    }
    setError(probe.message);
  }, [onSessionLost]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      if (probing.current) return;
      probing.current = true;
      void probeIdentity()
        .then((probe) => {
          if (probe.state !== "unauthorized") return;
          clearToken();
          onSessionLost();
        })
        .finally(() => {
          probing.current = false;
        });
    });
    return () => setUnauthorizedHandler(null);
  }, [onSessionLost]);

  const value: SessionState = {
    grants,
    subject,
    loading,
    error,
    reload: () => void load(),
  };

  if (loading) {
    return <div className="p-6 text-sm text-kw-text-muted">Identifying…</div>;
  }
  if (error) {
    return (
      <div className="p-6 space-y-3">
        <div className="text-sm text-red-400">Could not resolve your identity: {error}</div>
        <button
          onClick={value.reload}
          className="px-3 py-1.5 rounded bg-kw-l3 text-white text-sm"
        >
          Retry
        </button>
      </div>
    );
  }

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession must be used inside a SessionProvider");
  return ctx;
}

export function useGrants(): Grants {
  return useSession().grants;
}
